import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import ReactFlow, {
    MiniMap,
    Controls,
    Background,
    useNodesState,
    useEdgesState,
    addEdge,
    Panel,
    ReactFlowProvider,
    Handle,
    Position
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

// Custom Node Components
const VariableNode = ({ data, isConnectable, selected }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editLabel, setEditLabel] = useState(data.label);
    const [editValue, setEditValue] = useState(data.value || '');

    const handleLabelSave = () => {
        data.label = editLabel;
        setIsEditing(false);
    };

    const handleValueSave = () => {
        data.value = editValue ? parseFloat(editValue) : undefined;
    };

    return (
        <div className={`px-4 py-2 shadow-md rounded-md bg-blue-100 border-2 min-w-[120px] ${
            selected ? 'border-blue-500 ring-2 ring-blue-300' : 'border-blue-300'
        }`}>
            <Handle type="target" position={Position.Top} isConnectable={isConnectable} />
            
            {/* Delete button */}
            {selected && (
                <button
                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs hover:bg-red-600"
                    onClick={(e) => {
                        e.stopPropagation();
                        data.onDelete && data.onDelete();
                    }}
                >
                    ×
                </button>
            )}
            
            <div className="flex items-center">
                <div className="rounded-full w-8 h-8 flex justify-center items-center bg-blue-500 mr-2">
                    <span className="text-white text-xs font-bold">VAR</span>
                </div>
                <div className="flex-1">
                    {isEditing ? (
                        <input
                            type="text"
                            value={editLabel}
                            onChange={(e) => setEditLabel(e.target.value)}
                            onBlur={handleLabelSave}
                            onKeyPress={(e) => e.key === 'Enter' && handleLabelSave()}
                            className="text-sm font-bold text-blue-800 bg-white border rounded px-1 w-full"
                            autoFocus
                        />
                    ) : (
                        <div 
                            className="text-sm font-bold text-blue-800 cursor-pointer hover:bg-blue-200 px-1 rounded"
                            onClick={() => setIsEditing(true)}
                        >
                            {data.label}
                        </div>
                    )}
                    <div className="text-xs text-gray-500">{data.fieldType || 'Variable'}</div>
                </div>
            </div>
            
            {/* Manual value input */}
            <div className="mt-2">
                <input
                    type="number"
                    placeholder="Manual value (optional)"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={handleValueSave}
                    className="text-xs w-full border rounded px-1 py-1"
                />
                {data.value !== undefined && (
                    <div className="text-xs text-gray-600 mt-1">Value: {data.value}</div>
                )}
            </div>
            
            <Handle type="source" position={Position.Bottom} isConnectable={isConnectable} />
        </div>
    );
};

const OperatorNode = ({ data, isConnectable, selected }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [selectedOperator, setSelectedOperator] = useState(data.operator);

    const operators = [
        { symbol: '+', name: 'Add' },
        { symbol: '-', name: 'Subtract' },
        { symbol: '*', name: 'Multiply' },
        { symbol: '/', name: 'Divide' },
        { symbol: '%', name: 'Percentage' },
        { symbol: '^', name: 'Power' }
    ];

    const handleOperatorChange = (newOperator) => {
        data.operator = newOperator;
        setSelectedOperator(newOperator);
        setIsEditing(false);
    };

    const getOperatorName = (op) => {
        const found = operators.find(o => o.symbol === op);
        return found ? found.name : 'Operation';
    };

    return (
        <div className={`px-4 py-2 shadow-md rounded-md bg-orange-100 border-2 min-w-[80px] ${
            selected ? 'border-orange-500 ring-2 ring-orange-300' : 'border-orange-300'
        }`}>
            <Handle type="target" position={Position.Top} isConnectable={isConnectable} />
            
            {/* Delete button */}
            {selected && (
                <button
                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs hover:bg-red-600"
                    onClick={(e) => {
                        e.stopPropagation();
                        data.onDelete && data.onDelete();
                    }}
                >
                    ×
                </button>
            )}
            
            <div className="flex items-center justify-center">
                {isEditing ? (
                    <select
                        value={selectedOperator}
                        onChange={(e) => handleOperatorChange(e.target.value)}
                        onBlur={() => setIsEditing(false)}
                        className="text-sm bg-white border rounded px-1"
                        autoFocus
                    >
                        {operators.map(op => (
                            <option key={op.symbol} value={op.symbol}>
                                {op.symbol} ({op.name})
                            </option>
                        ))}
                    </select>
                ) : (
                    <div 
                        className="rounded-full w-8 h-8 flex justify-center items-center bg-orange-500 cursor-pointer hover:bg-orange-600"
                        onClick={() => setIsEditing(true)}
                    >
                        <span className="text-white text-lg font-bold">{data.operator}</span>
                    </div>
                )}
            </div>
            <div className="text-xs text-center text-orange-800 mt-1">{getOperatorName(data.operator)}</div>
            <Handle type="source" position={Position.Bottom} isConnectable={isConnectable} />
        </div>
    );
};

const OutputNode = ({ data, isConnectable, selected }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editLabel, setEditLabel] = useState(data.label);

    const handleLabelSave = () => {
        data.label = editLabel;
        setIsEditing(false);
    };

    return (
        <div className={`px-4 py-2 shadow-md rounded-md bg-green-100 border-2 min-w-[140px] ${
            selected ? 'border-green-500 ring-2 ring-green-300' : 'border-green-300'
        }`}>
            <Handle type="target" position={Position.Top} isConnectable={isConnectable} />
            
            {/* Delete button */}
            {selected && (
                <button
                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs hover:bg-red-600"
                    onClick={(e) => {
                        e.stopPropagation();
                        data.onDelete && data.onDelete();
                    }}
                >
                    ×
                </button>
            )}
            
            <div className="flex items-center">
                <div className="rounded-full w-8 h-8 flex justify-center items-center bg-green-500 mr-2">
                    <span className="text-white text-xs font-bold">OUT</span>
                </div>
                <div className="flex-1">
                    {isEditing ? (
                        <input
                            type="text"
                            value={editLabel}
                            onChange={(e) => setEditLabel(e.target.value)}
                            onBlur={handleLabelSave}
                            onKeyPress={(e) => e.key === 'Enter' && handleLabelSave()}
                            className="text-sm font-bold text-green-800 bg-white border rounded px-1 w-full"
                            autoFocus
                        />
                    ) : (
                        <div 
                            className="text-sm font-bold text-green-800 cursor-pointer hover:bg-green-200 px-1 rounded"
                            onClick={() => setIsEditing(true)}
                        >
                            {data.label}
                        </div>
                    )}
                    <div className="text-xs text-gray-500">Output Field</div>
                </div>
            </div>
            {data.value !== undefined && (
                <div className="text-xs text-gray-600 mt-1">Result: {data.value}</div>
            )}
        </div>
    );
};

const ConstantNode = ({ data, isConnectable, selected }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(data.value);

    const handleValueSave = () => {
        data.value = parseFloat(editValue) || 0;
        setIsEditing(false);
    };

    return (
        <div className={`px-4 py-2 shadow-md rounded-md bg-purple-100 border-2 min-w-[80px] ${
            selected ? 'border-purple-500 ring-2 ring-purple-300' : 'border-purple-300'
        }`}>
            <Handle type="target" position={Position.Top} isConnectable={isConnectable} />
            
            {/* Delete button */}
            {selected && (
                <button
                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs hover:bg-red-600"
                    onClick={(e) => {
                        e.stopPropagation();
                        data.onDelete && data.onDelete();
                    }}
                >
                    ×
                </button>
            )}
            
            <div className="flex items-center justify-center">
                {isEditing ? (
                    <input
                        type="number"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={handleValueSave}
                        onKeyPress={(e) => e.key === 'Enter' && handleValueSave()}
                        className="text-xs bg-white border rounded px-1 w-16 text-center"
                        autoFocus
                    />
                ) : (
                    <div 
                        className="rounded-full w-8 h-8 flex justify-center items-center bg-purple-500 cursor-pointer hover:bg-purple-600"
                        onClick={() => setIsEditing(true)}
                    >
                        <span className="text-white text-xs font-bold">{data.value}</span>
                    </div>
                )}
            </div>
            <div className="text-xs text-center text-purple-800 mt-1">Constant</div>
            <Handle type="source" position={Position.Bottom} isConnectable={isConnectable} />
        </div>
    );
};

// Node types
const nodeTypes = {
    variable: VariableNode,
    operator: OperatorNode,
    output: OutputNode,
    constant: ConstantNode,
};

// Default canvas templates for scenarios
const getDefaultCanvasTemplate = (scenarioId) => {
    const templates = {
        'SN001': {
            nodes: [
                { id: 'n1', type: 'variable', position: { x: 100, y: 50 }, data: { label: 'singleUnitPrice', fieldType: 'number' } },
                { id: 'n2', type: 'variable', position: { x: 300, y: 50 }, data: { label: 'quantity', fieldType: 'number' } },
                { id: 'n3', type: 'operator', position: { x: 200, y: 150 }, data: { operator: '*' } },
                { id: 'n4', type: 'output', position: { x: 200, y: 250 }, data: { label: 'valueSalesExcludingST' } },
                { id: 'n5', type: 'variable', position: { x: 400, y: 150 }, data: { label: 'taxRate', fieldType: 'number' } },
                { id: 'n6', type: 'constant', position: { x: 500, y: 150 }, data: { value: 100 } },
                { id: 'n7', type: 'operator', position: { x: 450, y: 250 }, data: { operator: '/' } },
                { id: 'n8', type: 'operator', position: { x: 325, y: 350 }, data: { operator: '*' } },
                { id: 'n9', type: 'output', position: { x: 325, y: 450 }, data: { label: 'salesTaxApplicable' } }
            ],
            edges: [
                { id: 'e1-3', source: 'n1', target: 'n3' },
                { id: 'e2-3', source: 'n2', target: 'n3' },
                { id: 'e3-4', source: 'n3', target: 'n4' },
                { id: 'e5-7', source: 'n5', target: 'n7' },
                { id: 'e6-7', source: 'n6', target: 'n7' },
                { id: 'e3-8', source: 'n3', target: 'n8' },
                { id: 'e7-8', source: 'n7', target: 'n8' },
                { id: 'e8-9', source: 'n8', target: 'n9' }
            ]
        },
        'SN002': {
            nodes: [
                { id: 'n1', type: 'variable', position: { x: 100, y: 50 }, data: { label: 'singleUnitPrice', fieldType: 'number' } },
                { id: 'n2', type: 'variable', position: { x: 300, y: 50 }, data: { label: 'quantity', fieldType: 'number' } },
                { id: 'n3', type: 'operator', position: { x: 200, y: 150 }, data: { operator: '*' } },
                { id: 'n4', type: 'output', position: { x: 200, y: 250 }, data: { label: 'valueSalesExcludingST' } },
                { id: 'n5', type: 'variable', position: { x: 400, y: 150 }, data: { label: 'taxRate', fieldType: 'number' } },
                { id: 'n6', type: 'constant', position: { x: 500, y: 150 }, data: { value: 100 } },
                { id: 'n7', type: 'operator', position: { x: 450, y: 250 }, data: { operator: '/' } },
                { id: 'n8', type: 'operator', position: { x: 325, y: 350 }, data: { operator: '*' } },
                { id: 'n9', type: 'output', position: { x: 325, y: 450 }, data: { label: 'salesTaxApplicable' } }
            ],
            edges: [
                { id: 'e1-3', source: 'n1', target: 'n3' },
                { id: 'e2-3', source: 'n2', target: 'n3' },
                { id: 'e3-4', source: 'n3', target: 'n4' },
                { id: 'e5-7', source: 'n5', target: 'n7' },
                { id: 'e6-7', source: 'n6', target: 'n7' },
                { id: 'e3-8', source: 'n3', target: 'n8' },
                { id: 'e7-8', source: 'n7', target: 'n8' },
                { id: 'e8-9', source: 'n8', target: 'n9' }
            ]
        },
        'SN008': {
            nodes: [
                { id: 'n1', type: 'variable', position: { x: 100, y: 50 }, data: { label: 'fixedNotifiedValueOrRetailPrice', fieldType: 'number' } },
                { id: 'n2', type: 'variable', position: { x: 400, y: 50 }, data: { label: 'quantity', fieldType: 'number' } },
                { id: 'n3', type: 'variable', position: { x: 250, y: 150 }, data: { label: 'taxRate', fieldType: 'number' } },
                { id: 'n4', type: 'constant', position: { x: 350, y: 150 }, data: { value: 100 } },
                { id: 'n5', type: 'operator', position: { x: 300, y: 250 }, data: { operator: '/' } },
                { id: 'n6', type: 'operator', position: { x: 200, y: 350 }, data: { operator: '*' } },
                { id: 'n7', type: 'operator', position: { x: 300, y: 450 }, data: { operator: '*' } },
                { id: 'n8', type: 'output', position: { x: 300, y: 550 }, data: { label: 'salesTaxApplicable' } }
            ],
            edges: [
                { id: 'e3-5', source: 'n3', target: 'n5' },
                { id: 'e4-5', source: 'n4', target: 'n5' },
                { id: 'e1-6', source: 'n1', target: 'n6' },
                { id: 'e5-6', source: 'n5', target: 'n6' },
                { id: 'e6-7', source: 'n6', target: 'n7' },
                { id: 'e2-7', source: 'n2', target: 'n7' },
                { id: 'e7-8', source: 'n7', target: 'n8' }
            ]
        }
    };

    // Return template or default standard rate template
    return templates[scenarioId] || templates['SN001'];
};

const CanvasBuilderPage = () => {
    const { scenarioId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [scenarios, setScenarios] = useState([]);
    const [selectedScenario, setSelectedScenario] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // React Flow state
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);

    // Check if user is admin
    const isAdmin = user?.role === 'admin';

    const deleteNode = useCallback((nodeId) => {
        if (!isAdmin) {
            toast.error('Only administrators can delete nodes');
            return;
        }

        setNodes((currentNodes) => currentNodes.filter((node) => node.id !== nodeId));
        setEdges((currentEdges) => currentEdges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
        toast.success('Node deleted successfully');
    }, [isAdmin, setEdges, setNodes]);

    const loadScenarios = useCallback(async () => {
        try {
            const response = await api.canvas.getScenarios();
            if (response.data.success) {
                setScenarios(response.data.data);
            }
        } catch (error) {
            console.error('Error loading scenarios:', error);
            toast.error('Failed to load scenarios');
        }
    }, []);

    const loadScenario = useCallback(async (id) => {
        try {
            const response = await api.canvas.getScenario(id);
            if (response.data.success) {
                const scenario = response.data.data;
                setSelectedScenario(scenario);

                // Load canvas configuration - priority order:
                // 1. Saved canvasConfig in scenarios table
                // 2. Template canvas from canvas_templates table  
                // 3. Default template for scenario
                let canvasConfig;
                
                console.log('Loading scenario:', scenario);
                console.log('Canvas config:', scenario.canvasConfig);
                console.log('Template canvas:', scenario.templateCanvas);
                
                if (scenario.canvasConfig) {
                    // Use saved canvas configuration from scenarios table
                    canvasConfig = typeof scenario.canvasConfig === 'string' 
                        ? JSON.parse(scenario.canvasConfig) 
                        : scenario.canvasConfig;
                    
                    // Handle nested canvasJson structure
                    if (canvasConfig.canvasJson) {
                        canvasConfig = canvasConfig.canvasJson;
                    }
                    console.log('Using saved canvas config');
                } else if (scenario.templateCanvas) {
                    // Use template canvas from canvas_templates table
                    canvasConfig = typeof scenario.templateCanvas === 'string'
                        ? JSON.parse(scenario.templateCanvas)
                        : scenario.templateCanvas;
                    
                    // Handle nested canvasJson structure
                    if (canvasConfig.canvasJson) {
                        canvasConfig = canvasConfig.canvasJson;
                    }
                    console.log('Using template canvas');
                } else {
                    // Pre-load default template for this scenario
                    canvasConfig = getDefaultCanvasTemplate(id);
                    console.log('Using default template');
                }

                // Add onDelete handlers to all nodes
                const nodesWithHandlers = (canvasConfig.nodes || []).map(node => ({
                    ...node,
                    data: {
                        ...node.data,
                        onDelete: () => deleteNode(node.id)
                    }
                }));

                setNodes(nodesWithHandlers);
                setEdges(canvasConfig.edges || []);
                
                toast.success(`Loaded scenario ${id} with ${canvasConfig.nodes?.length || 0} nodes`);
            }
        } catch (error) {
            console.error('Error loading scenario:', error);
            toast.error('Failed to load scenario');
        }
    }, [deleteNode, setEdges, setNodes]);

    useEffect(() => {
        loadScenarios();
    }, [loadScenarios]);

    useEffect(() => {
        if (scenarioId && scenarios.length > 0) {
            loadScenario(scenarioId);
        } else if (!scenarioId) {
            setSelectedScenario(null);
            setNodes([]);
            setEdges([]);
        }
        setLoading(false);
    }, [loadScenario, scenarioId, scenarios, setEdges, setNodes]);

    const onConnect = useCallback((params) => {
        setEdges((eds) => addEdge(params, eds));
    }, [setEdges]);

    const handleSaveCanvas = async () => {
        if (!isAdmin) {
            toast.error('Only administrators can save canvas configurations');
            return;
        }

        if (!selectedScenario) {
            toast.error('Please select a scenario first');
            return;
        }

        setSaving(true);
        try {
            // Clean up nodes data before saving (remove onDelete handlers)
            const cleanNodes = nodes.map(node => ({
                ...node,
                data: {
                    ...node.data,
                    onDelete: undefined // Remove function references
                }
            }));

            const canvasConfig = { 
                nodes: cleanNodes, 
                edges 
            };
            
            console.log('Saving canvas config:', canvasConfig);
            
            const response = await api.canvas.saveCanvas(selectedScenario.scenarioId, {
                canvasConfig,
                templateName: `${selectedScenario.scenarioId} Custom Template`
            });

            if (response.data.success) {
                toast.success('Canvas configuration saved successfully!');
                
                // Update the selected scenario to reflect the saved config
                setSelectedScenario(prev => ({
                    ...prev,
                    canvasConfig: canvasConfig
                }));
            }
        } catch (error) {
            console.error('Error saving canvas:', error);
            const errorMessage = error.response?.data?.message || 'Failed to save canvas configuration';
            toast.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const evaluateFormula = (nodes, edges) => {
        // Simple formula evaluation based on canvas structure
        const nodeMap = {};
        nodes.forEach(node => {
            nodeMap[node.id] = { ...node, result: null };
        });

        // Set default test values
        const defaultValues = {
            singleUnitPrice: 100,
            quantity: 2,
            taxRate: 18,
            fixedNotifiedValueOrRetailPrice: 200
        };

        // Set values for variable nodes
        nodes.forEach(node => {
            if (node.type === 'variable') {
                if (node.data.value !== undefined) {
                    nodeMap[node.id].result = node.data.value;
                } else if (defaultValues[node.data.label]) {
                    nodeMap[node.id].result = defaultValues[node.data.label];
                } else {
                    nodeMap[node.id].result = 1; // Default fallback
                }
            } else if (node.type === 'constant') {
                nodeMap[node.id].result = node.data.value;
            }
        });

        // Process operations in topological order (simplified)
        let changed = true;
        let iterations = 0;
        while (changed && iterations < 10) {
            changed = false;
            iterations++;

            for (const edge of edges) {
                const sourceNode = nodeMap[edge.source];
                const targetNode = nodeMap[edge.target];

                if (sourceNode && targetNode && sourceNode.result !== null) {
                    if (targetNode.type === 'operator') {
                        // For operators, we need to wait for all inputs
                        const inputEdges = edges.filter(e => e.target === targetNode.id);
                        const inputValues = inputEdges.map(e => nodeMap[e.source]?.result).filter(v => v !== null);
                        
                        if (inputValues.length >= 2) {
                            let result;
                            switch (targetNode.data.operator) {
                                case '+':
                                    result = inputValues.reduce((a, b) => a + b, 0);
                                    break;
                                case '-':
                                    result = inputValues.reduce((a, b) => a - b);
                                    break;
                                case '*':
                                    result = inputValues.reduce((a, b) => a * b, 1);
                                    break;
                                case '/':
                                    result = inputValues.reduce((a, b) => a / b);
                                    break;
                                case '%':
                                    result = inputValues.reduce((a, b) => a % b);
                                    break;
                                default:
                                    result = inputValues[0];
                            }
                            
                            if (targetNode.result !== result) {
                                nodeMap[targetNode.id].result = result;
                                changed = true;
                            }
                        }
                    } else if (targetNode.type === 'output') {
                        if (targetNode.result !== sourceNode.result) {
                            nodeMap[targetNode.id].result = sourceNode.result;
                            changed = true;
                        }
                    }
                }
            }
        }

        return nodeMap;
    };

    const handleTestFormula = async () => {
        if (!selectedScenario) {
            toast.error('Please select a scenario first');
            return;
        }

        try {
            // Evaluate formula locally first
            const results = evaluateFormula(nodes, edges);
            
            // Update nodes with calculated values
            const updatedNodes = nodes.map(node => {
                const result = results[node.id];
                if (result && result.result !== null) {
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            value: result.result
                        }
                    };
                }
                return node;
            });
            
            setNodes(updatedNodes);

            // Show results
            const outputNodes = updatedNodes.filter(node => node.type === 'output');
            if (outputNodes.length > 0) {
                const resultText = outputNodes.map(node => 
                    `${node.data.label}: ${node.data.value?.toFixed(2) || 'N/A'}`
                ).join(', ');
                toast.success(`Formula test completed! Results: ${resultText}`);
            } else {
                toast.success('Formula test completed! Check node values on canvas.');
            }

        } catch (error) {
            console.error('Error testing formula:', error);
            toast.error('Formula test failed: ' + error.message);
        }
    };

    const addNode = (nodeType) => {
        if (!isAdmin) {
            toast.error('Only administrators can edit canvas');
            return;
        }

        const newNodeId = `n${Date.now()}`;
        const position = { x: Math.random() * 400 + 100, y: Math.random() * 300 + 100 };

        let newNode;
        switch (nodeType) {
            case 'variable':
                newNode = {
                    id: newNodeId,
                    type: 'variable',
                    position,
                    data: { 
                        label: 'newVariable', 
                        fieldType: 'number',
                        onDelete: () => deleteNode(newNodeId)
                    }
                };
                break;
            case 'operator':
                newNode = {
                    id: newNodeId,
                    type: 'operator',
                    position,
                    data: { 
                        operator: '+',
                        onDelete: () => deleteNode(newNodeId)
                    }
                };
                break;
            case 'constant':
                newNode = {
                    id: newNodeId,
                    type: 'constant',
                    position,
                    data: { 
                        value: 0,
                        onDelete: () => deleteNode(newNodeId)
                    }
                };
                break;
            case 'output':
                newNode = {
                    id: newNodeId,
                    type: 'output',
                    position,
                    data: { 
                        label: 'newOutput',
                        onDelete: () => deleteNode(newNodeId)
                    }
                };
                break;
            default:
                return;
        }

        setNodes((nds) => [...nds, newNode]);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading Canvas Builder...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white shadow">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-4">
                        <div className="flex items-center">
                            <h1 className="text-2xl font-bold text-gray-900">
                                Canvas Builder - Drag & Drop Formula Editor
                            </h1>
                            {!isAdmin && (
                                <span className="ml-4 px-3 py-1 bg-yellow-100 text-yellow-800 text-sm rounded-full">
                                    Read Only
                                </span>
                            )}
                        </div>
                        
                        <div className="flex items-center space-x-4">
                            {/* Scenario selector */}
                            <select
                                value={scenarioId || ''}
                                onChange={(e) => {
                                    if (e.target.value) {
                                        navigate(`/app/canvas/${e.target.value}`);
                                    } else {
                                        navigate('/app/canvas');
                                    }
                                }}
                                className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            >
                                <option value="">Select Scenario</option>
                                {scenarios.map(scenario => (
                                    <option key={scenario.scenarioId} value={scenario.scenarioId}>
                                        {scenario.scenarioId} - {scenario.description?.substring(0, 50)}...
                                    </option>
                                ))}
                            </select>

                            <button
                                onClick={() => navigate('/app/dashboard')}
                                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                            >
                                Back to Dashboard
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex h-screen">
                {/* Sidebar - Node Palette */}
                {isAdmin && (
                    <div className="w-64 bg-white shadow-lg p-4">
                        <h3 className="text-lg font-semibold mb-4">Node Palette</h3>
                        
                        <div className="space-y-2">
                            <button
                                onClick={() => addNode('variable')}
                                className="w-full p-3 bg-blue-100 border-2 border-blue-300 rounded-md hover:bg-blue-200 text-left"
                            >
                                <div className="flex items-center">
                                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center mr-2">
                                        <span className="text-white text-xs font-bold">V</span>
                                    </div>
                                    <span className="text-sm font-medium">Variable</span>
                                </div>
                                <div className="text-xs text-gray-500 mt-1">Click name to edit, add manual values</div>
                            </button>

                            <button
                                onClick={() => addNode('operator')}
                                className="w-full p-3 bg-orange-100 border-2 border-orange-300 rounded-md hover:bg-orange-200 text-left"
                            >
                                <div className="flex items-center">
                                    <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center mr-2">
                                        <span className="text-white text-xs font-bold">+</span>
                                    </div>
                                    <span className="text-sm font-medium">Operator</span>
                                </div>
                                <div className="text-xs text-gray-500 mt-1">Click to change: +, -, *, /, %, ^</div>
                            </button>

                            <button
                                onClick={() => addNode('constant')}
                                className="w-full p-3 bg-purple-100 border-2 border-purple-300 rounded-md hover:bg-purple-200 text-left"
                            >
                                <div className="flex items-center">
                                    <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center mr-2">
                                        <span className="text-white text-xs font-bold">C</span>
                                    </div>
                                    <span className="text-sm font-medium">Constant</span>
                                </div>
                                <div className="text-xs text-gray-500 mt-1">Click value to edit number</div>
                            </button>

                            <button
                                onClick={() => addNode('output')}
                                className="w-full p-3 bg-green-100 border-2 border-green-300 rounded-md hover:bg-green-200 text-left"
                            >
                                <div className="flex items-center">
                                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-2">
                                        <span className="text-white text-xs font-bold">O</span>
                                    </div>
                                    <span className="text-sm font-medium">Output</span>
                                </div>
                                <div className="text-xs text-gray-500 mt-1">Click name to edit result field</div>
                            </button>
                        </div>

                        <div className="mt-4 p-3 bg-gray-50 rounded-md">
                            <h4 className="font-medium text-sm mb-2">How to Use</h4>
                            <div className="text-xs text-gray-600 space-y-1">
                                <div>• <strong>Add:</strong> Click buttons above</div>
                                <div>• <strong>Edit:</strong> Click on node elements</div>
                                <div>• <strong>Connect:</strong> Drag between node handles</div>
                                <div>• <strong>Delete:</strong> Select node, click red × button</div>
                                <div>• <strong>Test:</strong> Uses default values (price=100, qty=2)</div>
                            </div>
                        </div>

                        {selectedScenario && (
                            <div className="mt-6 pt-4 border-t">
                                <h4 className="font-medium mb-2">Actions</h4>
                                <div className="space-y-2">
                                    <button
                                        onClick={handleSaveCanvas}
                                        disabled={saving}
                                        className="w-full px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                                    >
                                        {saving ? 'Saving...' : 'Save Canvas'}
                                    </button>
                                    <button
                                        onClick={handleTestFormula}
                                        className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                                    >
                                        Test Formula
                                    </button>
                                </div>
                            </div>
                        )}

                        {selectedScenario && (
                            <div className="mt-4 p-3 bg-gray-50 rounded-md">
                                <h4 className="font-medium text-sm mb-1">Current Scenario</h4>
                                <div className="text-xs text-gray-600">
                                    <div><strong>ID:</strong> {selectedScenario.scenarioId}</div>
                                    <div><strong>Rate:</strong> {selectedScenario.taxRate || selectedScenario.rate}</div>
                                    <div><strong>Type:</strong> {selectedScenario.saleType}</div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Main Canvas Area */}
                <div className="flex-1">
                    {!selectedScenario ? (
                        <div className="h-full flex items-center justify-center">
                            <div className="text-center">
                                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                                    Select a Scenario to Start Building
                                </h2>
                                <p className="text-gray-600 mb-6">
                                    Choose a scenario from the dropdown above to load its pre-built canvas template.
                                    You can then drag and drop nodes to customize the formula.
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl">
                                    {scenarios.slice(0, 6).map(scenario => (
                                        <div
                                            key={scenario.scenarioId}
                                            className="border rounded-lg p-4 hover:shadow-md cursor-pointer transition-shadow"
                                            onClick={() => navigate(`/app/canvas/${scenario.scenarioId}`)}
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <h3 className="font-bold text-lg text-indigo-600">
                                                    {scenario.scenarioId}
                                                </h3>
                                                <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                                                    Pre-loaded
                                                </span>
                                            </div>
                                            <p className="text-gray-600 text-sm mb-2">
                                                {scenario.description?.substring(0, 80)}...
                                            </p>
                                            <div className="text-xs text-gray-500">
                                                <div>Rate: {scenario.taxRate || scenario.rate}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <ReactFlowProvider>
                            <ReactFlow
                                nodes={nodes}
                                edges={edges}
                                onNodesChange={onNodesChange}
                                onEdgesChange={onEdgesChange}
                                onConnect={onConnect}
                                nodeTypes={nodeTypes}
                                fitView
                                className="bg-gray-100"
                            >
                                <Controls />
                                <MiniMap />
                                <Background variant="dots" gap={12} size={1} />
                                <Panel position="top-right">
                                    <div className="bg-white p-3 rounded-md shadow-md">
                                        <h4 className="font-medium text-sm mb-1">Canvas Info</h4>
                                        <div className="text-xs text-gray-600">
                                            <div>Nodes: {nodes.length}</div>
                                            <div>Connections: {edges.length}</div>
                                            <div>Scenario: {selectedScenario.scenarioId}</div>
                                        </div>
                                    </div>
                                </Panel>
                            </ReactFlow>
                        </ReactFlowProvider>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CanvasBuilderPage;
